import { Controller } from '@hotwired/stimulus'

export const LESSON_SEARCH_PROGRESS_MESSAGES = [
  'Understanding your search',
  'Scanning lesson details',
  'Checking technique relevance',
  'Comparing likely matches',
  'Ranking the strongest lessons',
  'Preparing your results',
]

export const LESSON_SEARCH_PROGRESS_INTERVAL_MS = 800

// Gives slower lesson searches a lightweight sense of forward movement. These
// labels describe the retrieval experience at a deliberately high level; they
// are decorative rather than live telemetry, so they stay out of the loading
// region's accessibility announcement.
export default class extends Controller {
  static targets = ['message']

  connect() {
    this.boundStart = this.start.bind(this)
    this.boundStop = this.stop.bind(this)
    this.boundVisibilityChange = this.scheduleVisibilityCheck.bind(this)
    this.element.addEventListener('lesson-search-progress:start', this.boundStart)
    this.element.addEventListener('lesson-search-progress:stop', this.boundStop)
    document.addEventListener('turbo:render', this.boundVisibilityChange)
    this.resetMessage()

    // Turbo previews and the app-level navigation shell can become visible
    // without this element receiving the original click event. Observe the
    // attributes that drive those CSS states, then check after two paint frames
    // so Safari has applied the preview before visibility is measured.
    this.visibilityObserver = new MutationObserver(this.boundVisibilityChange)
    this.visibilityObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-turbo-preview'],
    })
    this.observeVisibilityDriver(this.element.parentElement)
    this.observeVisibilityDriver(document.getElementById('search_results'))
    this.scheduleVisibilityCheck()
  }

  disconnect() {
    this.element.removeEventListener('lesson-search-progress:start', this.boundStart)
    this.element.removeEventListener('lesson-search-progress:stop', this.boundStop)
    document.removeEventListener('turbo:render', this.boundVisibilityChange)
    this.visibilityObserver?.disconnect()
    window.cancelAnimationFrame(this.visibilityFrame)
    this.stopTimer()
    this.progressActive = false
  }

  observeVisibilityDriver(element) {
    if (!element) return

    this.visibilityObserver.observe(element, {
      attributes: true,
      attributeFilter: ['class'],
    })
  }

  scheduleVisibilityCheck() {
    window.cancelAnimationFrame(this.visibilityFrame)
    this.visibilityFrame = window.requestAnimationFrame(() => {
      this.visibilityFrame = window.requestAnimationFrame(() => {
        this.visibilityFrame = null
        this.startIfVisible()
      })
    })
  }

  startIfVisible() {
    if (!this.progressActive && this.element.getClientRects().length > 0) this.start()
  }

  start() {
    if (!this.hasMessageTarget || this.progressActive) return

    this.progressActive = true
    this.stopTimer()
    this.messageIndex = 0
    this.renderMessage(false)
    if (this.reducedMotion) return

    this.progressTimer = window.setInterval(
      () => this.advanceMessage(),
      LESSON_SEARCH_PROGRESS_INTERVAL_MS
    )
  }

  stop() {
    this.progressActive = false
    this.stopTimer()
    this.resetMessage()
  }

  stopTimer() {
    if (!this.progressTimer) return

    window.clearInterval(this.progressTimer)
    this.progressTimer = null
  }

  resetMessage() {
    if (!this.hasMessageTarget) return

    this.messageIndex = 0
    this.renderMessage(false)
  }

  advanceMessage() {
    if (this.messageIndex >= LESSON_SEARCH_PROGRESS_MESSAGES.length - 1) {
      this.stopTimer()
      return
    }

    this.messageIndex += 1
    this.renderMessage(true)

    if (this.messageIndex === LESSON_SEARCH_PROGRESS_MESSAGES.length - 1) this.stopTimer()
  }

  renderMessage(animate) {
    this.messageTarget.textContent = LESSON_SEARCH_PROGRESS_MESSAGES[this.messageIndex]
    this.messageTarget.classList.remove('is-changing')
    if (!animate) return

    // Restart the short entrance animation for each new step.
    void this.messageTarget.offsetWidth
    this.messageTarget.classList.add('is-changing')
  }

  get reducedMotion() {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  }
}
